/* eslint-disable */
// ─── INHERITED IRA SCHEDULES ──────────────────────────────────────────────────
// Inherited IRAs carry their own RMD schedule and can never aggregate with the
// owner's own IRAs (or with a Sec. 204 annuity). They are excluded from both
// Scenario tabs and reported on their own lines in RMD Calculations and
// Projections.
//
// Account fields used:
//   is_inherited_ira    true
//   descendent_dob      decedent's DOB (legacy key name kept for saved data)
//   date_of_death       YYYY-MM-DD (optional; drives the after-RBD test)
//   beneficiary_type    "edb" (eligible designated — stretch)        [default]
//                       "dcb" (designated, non-eligible — 10-year rule)
//                       "non" (non-designated — 5-year / ghost life)
//   dob_selection       0 = use beneficiary (client) age, 1 = use decedent age
//                       (decedent age = "ghost" life expectancy, used when the
//                       beneficiary is older than the decedent or for "non")
//
// Method: single-life divisor set once in the year after death, then reduced by
// one each year (non-recalculated). Under the 2024 final regs a 10-year-rule
// beneficiary of a decedent who died on/after the RBD takes annual RMDs on the
// same single-life schedule for years 1–9 and empties the account in year 10.

import { getSingleLifePeriod, getRMDRequiredAge } from "./tables";

function yearOf(d) { return d ? new Date(d).getFullYear() : null; }

export function inheritedRule(acct) {
  const type = acct.beneficiary_type || "edb";
  const decedentBirth = yearOf(acct.descendent_dob);
  const deathYear = yearOf(acct.date_of_death);
  let afterRBD = null;
  if (decedentBirth && deathYear) {
    const rbdAge = getRMDRequiredAge(decedentBirth);
    afterRBD = (deathYear - decedentBirth) > rbdAge;        // RBD is April 1 of the year after reaching rmdAge
  }
  return { type, deathYear, decedentBirth, afterRBD };
}

// Divisor for the FIRST distribution year (year after death), anchored on the
// chosen life. If death year is unknown, anchor on the current year.
function firstDivisor(acct, clientDOB, currentYear) {
  const rule = inheritedRule(acct);
  const anchorYear = rule.deathYear ? rule.deathYear + 1 : currentYear;
  const useDecedent = acct.dob_selection === 1 || rule.type === "non";
  const refBirth = useDecedent ? rule.decedentBirth : yearOf(clientDOB);
  if (!refBirth) return { anchorYear, divisor: 0.001 };
  const ageInAnchor = anchorYear - refBirth;
  return { anchorYear, divisor: getSingleLifePeriod(ageInAnchor) || 0.001 };
}

// Current-year RMD for one inherited account.
export function inheritedRMDForYear(acct, clientDOB, year) {
  const rule = inheritedRule(acct);
  const { anchorYear, divisor: d0 } = firstDivisor(acct, clientDOB, year);
  const yearsSince = Math.max(0, year - anchorYear);
  const balance = acct.balance || 0;
  const divisor = Math.max(1, d0 - yearsSince);
  const yearIndex = rule.deathYear ? year - rule.deathYear : null;   // 1 = first year after death

  if (rule.type === "dcb") {
    if (yearIndex !== null && yearIndex >= 10) return { divisor: 1, rmd: balance, note: "10-year rule — full distribution", required: true };
    if (rule.afterRBD === false) return { divisor: null, rmd: 0, note: "10-year rule — no annual RMD (decedent died before RBD); empty by year 10", required: false };
    return { divisor, rmd: balance / divisor, note: "10-year rule — annual RMD (decedent died after RBD)", required: true };
  }
  if (rule.type === "non") {
    if (rule.afterRBD === false) {
      if (yearIndex !== null && yearIndex >= 5) return { divisor: 1, rmd: balance, note: "5-year rule — full distribution", required: true };
      return { divisor: null, rmd: 0, note: "5-year rule — no annual RMD; empty by year 5", required: false };
    }
    return { divisor, rmd: balance / divisor, note: "Ghost life expectancy (decedent's)", required: true };
  }
  return { divisor, rmd: balance / divisor, note: "Stretch — single life, reduced by 1 each year", required: true };
}

// Multi-year projection for one inherited account.
export function projectInherited(acct, clientDOB, currentYear, growthRate, horizon = 10) {
  const rule = inheritedRule(acct);
  let bal = acct.balance || 0;
  const rows = [];
  for (let i = 0; i < horizon; i++) {
    const year = currentYear + i + 1;
    const r = inheritedRMDForYear({ ...acct, balance: bal }, clientDOB, year);
    const rmd = Math.min(bal, r.rmd);
    const end = Math.max(0, (bal - rmd) * (1 + growthRate));
    rows.push({ Year: year, StartValue: bal, Divisor: r.divisor, RMD: rmd, EndValue: end, Note: r.note });
    bal = end;
  }
  return { rows, rule };
}
